import { Link, getRouteApi } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { NavBreadcrumbs } from "@/components/NavBreadcrumbs";
import "./events.group.$groupId.css";

const routeApi = getRouteApi("/events/group/$groupId");

const raceTypeDisplayMap: Record<string, string> = {
  triathlon: "Triathlon",
  duathlon: "Duathlon",
};

function formatRaceTypeLabel(raceType: string | null | undefined): string {
  if (!raceType) return "Results";
  const normalized = raceType.trim().toLowerCase();
  const mapped = raceTypeDisplayMap[normalized];
  if (mapped) return mapped;

  const kMatch = normalized.match(/^(\d+(?:\.\d+)?)k$/);
  if (kMatch) return `${kMatch[1]}K`;

  return normalized
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(" ");
}

function formatYearWindow(
  firstYear: number | null,
  lastYear: number | null,
): string | null {
  if (firstYear == null && lastYear == null) return null;
  const start = firstYear ?? lastYear;
  const end = lastYear ?? firstYear;
  if (start == null || end == null) return null;
  return start === end ? String(start) : `${start}–${end}`;
}

export default function EventGroupPage() {
  const { groupId } = routeApi.useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ["event-group", groupId],
    queryFn: async () => {
      const { data, error } = await api.GET("/events/group/{groupId}", {
        params: { path: { groupId } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="event-group-page">
        <div className="event-group-hero animate-pulse">
          <div className="mb-3 h-8 w-1/3 rounded bg-muted/70" />
          <div className="h-4 w-1/4 rounded bg-muted/50" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="event-group-page">
        <NavBreadcrumbs
          items={[{ label: "Records", to: "/records", search: {} }]}
          current="Event Group"
        />
        <p className="event-group-error">
          {error instanceof Error ? error.message : "Error loading event group"}
        </p>
      </div>
    );
  }

  const { group, series } = data;
  const sortedSeries = [...series].sort(
    (a, b) => b.totalResults - a.totalResults,
  );

  return (
    <div className="event-group-page">
      <header className="event-group-hero">
        <NavBreadcrumbs
          items={[{ label: "Records", to: "/records", search: {} }]}
          current={group.displayName}
        />
        <div className="event-group-hero-copy">
          <p className="event-group-eyebrow">Event Group</p>
          <h1 className="event-group-title">{group.displayName}</h1>
          {group.brand ? (
            <p className="event-group-meta">{group.brand}</p>
          ) : null}
        </div>
      </header>

      <section className="event-group-section" aria-label="Sub-events">
        <div className="event-group-section-header">
          <div>
            <h2 className="event-group-section-title">Races</h2>
            <p className="event-group-section-copy">
              Each race in this event has its own series page with full results
              history.
            </p>
          </div>
        </div>

        {sortedSeries.length === 0 ? (
          <div className="event-group-empty">
            <p>No sub-event series are linked to this group yet.</p>
          </div>
        ) : (
          <div className="event-group-series-grid">
            {sortedSeries.map((s) => {
              const window = formatYearWindow(s.firstYear, s.lastYear);
              return (
                <Link
                  key={s.id}
                  to="/events/series/$seriesId"
                  params={{ seriesId: s.id }}
                  search={{ year: undefined }}
                  className="event-group-series-card"
                  aria-label={`Open ${s.subEventLabel ?? formatRaceTypeLabel(s.raceType)} series page`}
                >
                  <p className="event-group-series-eyebrow">
                    {formatRaceTypeLabel(s.raceType)}
                  </p>
                  <h3 className="event-group-series-title">
                    {s.subEventLabel ?? s.displayName}
                  </h3>
                  <div className="event-group-series-stats">
                    <div>
                      <span className="event-group-series-stat-value">
                        {s.totalEvents.toLocaleString()}
                      </span>
                      <span className="event-group-series-stat-label">
                        {s.totalEvents === 1 ? "year" : "years"}
                      </span>
                    </div>
                    <div>
                      <span className="event-group-series-stat-value">
                        {s.totalResults.toLocaleString()}
                      </span>
                      <span className="event-group-series-stat-label">
                        results
                      </span>
                    </div>
                    {window ? (
                      <div>
                        <span className="event-group-series-stat-value">
                          {window}
                        </span>
                        <span className="event-group-series-stat-label">
                          window
                        </span>
                      </div>
                    ) : null}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
